import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { ToastrService } from 'ngx-toastr';
import { AzureAuthService } from 'src/service/azure-auth.service';
import { DataService, CustomerService, SiteTypeService, DashboardService, TicketService, ConnectorService } from '../../../api/services';
import { Customer } from '../../../api/models/customer';
import { CustomerSettings, Ticket, Connector } from '../../../api/models';
import { ApplicationConfigService } from 'src/service/application-config.service';
import { BsDropdownConfig } from 'ngx-bootstrap/dropdown';

@Component({
  selector: 'app-organisational-settings',
  templateUrl: './organisational-settings.component.html',
  styleUrls: ['./organisational-settings.component.css'],
  providers: [{ provide: BsDropdownConfig, useValue: { isAnimated: true, autoClose: true } }]
})
export class OrganisationalSettingsComponent implements OnInit {
  
  customer: Customer;
  settings: CustomerSettings = {} as CustomerSettings;
  supportLevel = 'No SLA Purchased';
  databaseTechnology = 'Unknown';
  private connectors: Connector[] = [];

  constructor(
    private dashboardService: DashboardService,
    private dataService: DataService,
    private route: ActivatedRoute,
    public app: ApplicationConfigService,
    private customerService: CustomerService,
    private siteTypeService: SiteTypeService,
    private ticketService: TicketService,
    private connectorService: ConnectorService,
    public azureAuthService: AzureAuthService,
    private toasterService: ToastrService) {
      app.customerChanged$.subscribe(cid => {
        this.ngOnInit();
      });
  }

  ngOnInit() {
    if (this.azureAuthService.isAdmin()){
      if (this.app.customers == null){
        this.customerService.CustomerGetAllCustomers().subscribe(c => {           
          this.app.customers = c;//.filter(c=>c.active);          
        });
      }
      if (this.app.customerId == null) {
        this.app.customerId = this.azureAuthService.userProfile.customerId;
        this.settings = {} as CustomerSettings;
        this.settings.customerId = this.app.customerId;
        this.settings.applicationName = this.app.applicationName;
        this.settings.backgroundImageUrl = this.app.backgroundImageUrl;
        this.settings.homeScreenMessage = this.app.homeScreen;
        this.settings.themeColour = this.app.themeColour;
        this.settings.logoUrl = this.app.logoUrl;
      }
      if (this.app.customerId){
        this.customerService.CustomerGetCustomerById(this.app.customerId).subscribe(r => {
          this.customer = r;
          this.setSupportLevelFromCustomer(this.customer);
          this.settings = {} as CustomerSettings;
          this.settings.customerId = this.app.customerId;
          this.settings.applicationName = this.app.applicationName;
          this.settings.backgroundImageUrl = this.app.backgroundImageUrl;
          this.settings.homeScreenMessage = this.app.homeScreen;
          this.settings.themeColour = this.app.themeColour;
          this.settings.logoUrl = this.app.logoUrl;
          this.resolveDatabaseTechnologyFromCustomer(this.customer);
        });
      }
    }else{
      this.app.customerId = this.azureAuthService.userProfile.customerId;
      this.settings = {} as CustomerSettings;
      this.settings.customerId = this.app.customerId;
      this.settings.applicationName = this.app.applicationName;
      this.settings.backgroundImageUrl = this.app.backgroundImageUrl;
      this.settings.homeScreenMessage = this.app.homeScreen;
      this.settings.logoUrl = this.app.logoUrl;
    }

    this.loadDatabaseTechnology();
    this.loadSupportLevel();
  }

  connectionString: string;
  connectionStringLoading: boolean;
  generateConnectionString(validForDays: number){
    this.connectionStringLoading = true;
    this.dataService.DataGetConnectionString({validForDays:365, customerId:this.app.customerId}).subscribe(s => {
      this.connectionString = s;
      this.toasterService.success("Connection String Generated");
      this.connectionStringLoading = false;
    })
  }

  changeThemeColour(colour){
    this.settings.themeColour = colour;
    this.app.themeColour = colour;
    document.body.setAttribute("class","theme-" + colour);
  }

  enableEdit(){
    this.edit = true;
  }
  saveChanges(){
    this.customerService.CustomerUpdateCustomerSettings(this.settings).subscribe(r => {
      this.edit = false;
      this.toasterService.success("Organisational Settings Updated");
    }, err => {
      this.toasterService.error("Failed to update Organisational Settings");
    });
  }
  edit: boolean = false;

  private loadSupportLevel(): void {
    if (!this.app.customerId) {
      this.supportLevel = 'No SLA Purchased';
      return;
    }

    if (this.setSupportLevelFromCustomer(this.customer)) {
      return;
    }

    this.ticketService.TicketGetCustomerTickets(this.app.customerId).subscribe(tickets => {
      this.supportLevel = this.resolveSupportLevelFromTickets(tickets);
    }, () => {
      this.supportLevel = 'No SLA Purchased';
    });
  }

  private loadDatabaseTechnology(): void {
    if (!this.app.customerId) {
      this.databaseTechnology = 'Unknown';
      return;
    }

    if (this.resolveDatabaseTechnologyFromCustomer(this.customer)) {
      return;
    }

    this.customerService.CustomerGetCustomerById(this.app.customerId).subscribe(customer => {
      this.customer = customer;
      this.resolveDatabaseTechnologyFromCustomer(customer);
    }, () => {
      this.databaseTechnology = 'Unknown';
    });
  }

  private resolveDatabaseTechnologyFromCustomer(customer?: Customer): boolean {
    const connectorId = customer?.timeSeriesConnectorId;
    if (!connectorId) {
      this.databaseTechnology = 'Unknown';
      return false;
    }

    this.resolveDatabaseTechnologyFromConnector(connectorId);
    return true;
  }

  private resolveDatabaseTechnologyFromConnector(connectorId: number): void {
    const applyConnector = (connector: Connector | undefined) => {
      this.databaseTechnology = connector?.timeSeriesApiType || connector?.name || `Connector ${connectorId}`;
    };

    if (this.connectors.length > 0) {
      applyConnector(this.connectors.find(connector => connector.connectorId === connectorId));
      return;
    }

    this.connectorService.ConnectorGet().subscribe(connectors => {
      this.connectors = connectors || [];
      applyConnector(this.connectors.find(connector => connector.connectorId === connectorId));
    }, () => {
      this.databaseTechnology = `Connector ${connectorId}`;
    });
  }

  private setSupportLevelFromCustomer(customer?: Customer): boolean {
    const source = customer as Customer & {
      slaLevel?: string;
      SlaLevel?: string;
      supportLevel?: string;
      SupportLevel?: string;
    };

    const level = (source?.slaLevel || source?.SlaLevel || source?.supportLevel || source?.SupportLevel || '').trim();
    if (!level) {
      return false;
    }

    this.supportLevel = level;
    return true;
  }

  private resolveSupportLevelFromTickets(tickets?: Ticket[] | null): string {
    const levels = (tickets || [])
      .map(ticket => (ticket.slaLevel || '').trim())
      .filter(level => !!level);

    if (levels.length === 0) {
      return 'No SLA Purchased';
    }

    const counts: { [level: string]: number } = {};
    for (const level of levels) {
      const key = level.toLowerCase();
      counts[key] = (counts[key] || 0) + 1;
    }

    let bestLevel = levels[0];
    let bestCount = 0;
    for (const level of levels) {
      const count = counts[level.toLowerCase()] || 0;
      if (count > bestCount) {
        bestCount = count;
        bestLevel = level;
      }
    }

    return bestLevel;
  }


}
